import { useState, useEffect, useRef } from 'react';
import useDebounce from 'hooks/useDebounce';
import { getValidateSerialDetails } from 'services/suscription/productExternal.service';
import { TValidationSerialResponse } from 'types/suscription/productExternal.types';
import { generateSerialValidationMessage } from 'utils/serialValidation.utils';

import './SerialInput.scss';

interface SerialInputProps {
  value?: string;
  onValueChange?: (serial: string) => void;
  onInputChange?: (serial: string) => void;
  onErrorChange?: (hasError: boolean) => void;
  onValidatingChange?: (isValidating: boolean) => void;
  placeholder?: string;
  customClassName?: string;
  label?: string;
  customWidth?: string;
  externalError?: string;
}

/**
 * Componente de input para validación de seriales con debounce
 * @param value - Valor actual del serial
 * @param onValueChange - Callback que se ejecuta cuando el serial es válido
 * @param onInputChange - Callback que se ejecuta en cada cambio del input (en tiempo real)
 * @param onErrorChange - Callback que se ejecuta cuando cambia el estado de error del serial
 * @param onValidatingChange - Callback que se ejecuta cuando cambia el estado de validación del serial
 * @param placeholder - Placeholder del input (opcional)
 * @param label - Label del input (opcional)
 * @param customClassName - Clases CSS adicionales
 * @param customWidth - Ancho del input (opcional, por defecto 300px)
 */
const SerialInput = ({
  value = '',
  onValueChange,
  onInputChange,
  onErrorChange,
  onValidatingChange,
  label = 'Serial',
  placeholder = 'Escribe el serial',
  customClassName = '',
  customWidth = '300px',
  externalError
}: SerialInputProps) => {
  const [errorResponse, setErrorResponse] = useState<TValidationSerialResponse | string | undefined>(undefined);
  const hasError = !!errorResponse || !!externalError;
  const displayError = externalError || errorResponse;
  const [localValue, setLocalValue] = useState(value);
  const debouncedValue = useDebounce(localValue, 1000);

  const onValueChangeRef = useRef(onValueChange);
  const onInputChangeRef = useRef(onInputChange);
  const onErrorChangeRef = useRef(onErrorChange);
  const onValidatingChangeRef = useRef(onValidatingChange);

  const updateCallbackRefs = () => {
    onValueChangeRef.current = onValueChange;
    onInputChangeRef.current = onInputChange;
    onErrorChangeRef.current = onErrorChange;
    onValidatingChangeRef.current = onValidatingChange;
  };

  const clearSerialError = () => {
    setErrorResponse(undefined);
    onErrorChangeRef.current?.(false);
  };

  const handleSerialValidationSuccess = (serial: string) => {
    onValueChangeRef.current?.(serial);
    clearSerialError();
  };

  const handleSerialValidationError = (errorResponse: TValidationSerialResponse | string) => {
    setErrorResponse(errorResponse);
    onErrorChangeRef.current?.(true);
  };

  const updateValidatingState = (isValidating: boolean) => {
    onValidatingChangeRef.current?.(isValidating);
  };

  const validateSerial = async (serial: string) => {
    try {
      updateValidatingState(true);
      const resp = await getValidateSerialDetails(serial);
      if (resp.isAvailable) {
        handleSerialValidationSuccess(serial);
      } else {
        handleSerialValidationError(resp);
      }
    } catch (error) {
      handleSerialValidationError('Error al validar el serial');
    } finally {
      updateValidatingState(false);
    }
  };

  const processSerialValidation = (serial: string) => {
    if (!serial || serial.trim() === '') {
      clearSerialError();
      return;
    }

    validateSerial(serial);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    onInputChangeRef.current?.(newValue);
    clearSerialError();
  };

  useEffect(() => {
    updateCallbackRefs();
  }, [onValueChange, onInputChange, onErrorChange, onValidatingChange]);

  useEffect(() => {
    processSerialValidation(debouncedValue);
  }, [debouncedValue]);

  useEffect(() => {
    if (value !== localValue) setLocalValue(value);
  }, [value]);

  return (
    <div
      className={`serialInputContainer ${hasError ? 'error' : ''} ${customClassName}`}
      style={{ width: customWidth }}
    >
      <label htmlFor="serialInput" className="serialInputLabel">
        {label}
      </label>
      <input
        id="serialInput"
        type="text"
        placeholder={placeholder}
        value={localValue}
        onChange={handleChange}
        style={{ width: customWidth }}
        className={`serialInput ${hasError ? 'error' : localValue ? 'filled' : ''}`}
      />
      {displayError && (
        <span className="serialValidationErrorMessage">
          {typeof displayError === 'string' ? displayError : generateSerialValidationMessage(displayError)}
        </span>
      )}
    </div>
  );
};

export default SerialInput;
